import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  addItemsToAlbum,
  createAlbum,
  deleteAlbum,
  getAlbumById,
  reorderAlbumItems,
  removeAlbumItem,
  searchAlbums,
  setAlbumCover,
  toggleAlbumActive,
  toggleAlbumHome,
  toggleAlbumItemVisible,
  toggleAlbumPublic,
  updateAlbum,
  updateAlbumItem,
} from "../../../../services/mediaAlbumServices";

import { MEDIA_ALBUM_INITIAL_FORM } from "../data/mediaAlbumsAdminData";

import {
  buildMediaAlbumSearchParams,
  getMediaAlbumStats,
  normalizeMediaAlbumForm,
  validateMediaAlbumForm,
} from "../utils/mediaAlbumsAdminUtils";

export default function useMediaAlbumsAdmin() {
  const mountedRef = useRef(true);

  const [q, setQ] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [homeFilter, setHomeFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [actionId, setActionId] = useState(null);
  const [photosActionId, setPhotosActionId] = useState(null);

  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const [formOpen, setFormOpen] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [form, setForm] = useState(MEDIA_ALBUM_INITIAL_FORM);
  const [fieldErrors, setFieldErrors] = useState({});

  const [deleteItem, setDeleteItem] = useState(null);

  const [photosAlbum, setPhotosAlbum] = useState(null);
  const [mediaPickerOpen, setMediaPickerOpen] = useState(false);

  const totalPages = useMemo(() => {
    return Math.max(Math.ceil(total / limit), 1);
  }, [total, limit]);

  const stats = useMemo(() => {
    return getMediaAlbumStats(items, total);
  }, [items, total]);

  const showNotice = useCallback((message) => {
    setNotice(message);

    window.clearTimeout(showNotice.timeout);
    showNotice.timeout = window.setTimeout(() => {
      setNotice("");
    }, 3500);
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const params = buildMediaAlbumSearchParams({
        q,
        activeFilter,
        homeFilter,
        page,
        limit,
      });

      const res = await searchAlbums(params);

      if (!mountedRef.current) return;

      setItems(Array.isArray(res?.items) ? res.items : []);
      setTotal(Number(res?.total || 0));
    } catch (fetchError) {
      console.error("Erreur chargement albums :", fetchError);

      if (!mountedRef.current) return;

      setItems([]);
      setTotal(0);
      setError("Impossible de charger les albums photos pour le moment.");
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [q, activeFilter, homeFilter, page, limit]);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const onSubmitSearch = useCallback(
    (event) => {
      event?.preventDefault?.();

      if (page !== 1) {
        setPage(1);
      } else {
        fetchData();
      }
    },
    [fetchData, page],
  );

  const changeActiveFilter = useCallback((value) => {
    setActiveFilter(value);
    setPage(1);
  }, []);

  const changeHomeFilter = useCallback((value) => {
    setHomeFilter(value);
    setPage(1);
  }, []);

  const changeLimit = useCallback((value) => {
    setLimit(Number(value) || 10);
    setPage(1);
  }, []);

  const resetFilters = useCallback(() => {
    setQ("");
    setActiveFilter("all");
    setHomeFilter("all");
    setLimit(10);
    setPage(1);
  }, []);

  const openCreate = useCallback(() => {
    setCurrentId(null);
    setForm(MEDIA_ALBUM_INITIAL_FORM);
    setFieldErrors({});
    setFormOpen(true);
  }, []);

  const openEdit = useCallback(async (id) => {
    if (!id) return;

    try {
      setSaving(true);
      setError("");

      const row = await getAlbumById(id);

      if (!row) {
        setError("Album introuvable.");
        return;
      }

      setCurrentId(id);
      setForm({
        title: row.title || "",
        slug: row.slug || "",
        description: row.description || "",
        is_active: !!row.is_active,
        is_public: !!row.is_public,
        show_on_home: !!row.show_on_home,
        display_order: Number(row.display_order || 0),
      });
      setFieldErrors({});
      setFormOpen(true);
    } catch (editError) {
      console.error("Erreur ouverture album :", editError);
      setError("Impossible d’ouvrir cet album.");
    } finally {
      setSaving(false);
    }
  }, []);

  const closeForm = useCallback(() => {
    setFormOpen(false);
    setCurrentId(null);
    setForm(MEDIA_ALBUM_INITIAL_FORM);
    setFieldErrors({});
  }, []);

  const updateFormField = useCallback((field, value) => {
    setForm((previous) => ({
      ...previous,
      [field]: value,
    }));

    setFieldErrors((previous) => {
      if (!previous[field]) return previous;

      const next = { ...previous };
      delete next[field];
      return next;
    });
  }, []);

  const onSave = useCallback(async () => {
    const errors = validateMediaAlbumForm(form);

    setFieldErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    try {
      setSaving(true);
      setError("");

      const payload = normalizeMediaAlbumForm(form);

      let savedAlbum;

      if (currentId) {
        savedAlbum = await updateAlbum(currentId, payload);
        showNotice("Album modifié avec succès.");
      } else {
        savedAlbum = await createAlbum(payload);
        showNotice("Album créé avec succès.");
      }

      closeForm();
      await fetchData();

      if (!currentId && savedAlbum?.id) {
        setPhotosAlbum(savedAlbum);
      }
    } catch (saveError) {
      console.error("Erreur enregistrement album :", saveError);
      setError("Impossible d’enregistrer cet album.");
    } finally {
      setSaving(false);
    }
  }, [closeForm, currentId, fetchData, form, showNotice]);

  const onToggleActive = useCallback(
    async (album) => {
      if (!album?.id) return;

      try {
        setActionId(`active-${album.id}`);
        setError("");

        await toggleAlbumActive(album);
        showNotice("Statut de l’album mis à jour.");
        await fetchData();
      } catch (toggleError) {
        console.error("Erreur changement statut album :", toggleError);
        setError("Impossible de modifier le statut de cet album.");
      } finally {
        setActionId(null);
      }
    },
    [fetchData, showNotice],
  );

  const onTogglePublic = useCallback(
    async (album) => {
      if (!album?.id) return;

      try {
        setActionId(`public-${album.id}`);
        setError("");

        await toggleAlbumPublic(album);
        showNotice("Visibilité publique mise à jour.");
        await fetchData();
      } catch (toggleError) {
        console.error("Erreur changement visibilité album :", toggleError);
        setError("Impossible de modifier la visibilité de cet album.");
      } finally {
        setActionId(null);
      }
    },
    [fetchData, showNotice],
  );

  const onToggleHome = useCallback(
    async (album) => {
      if (!album?.id) return;

      try {
        setActionId(`home-${album.id}`);
        setError("");

        await toggleAlbumHome(album);
        showNotice("Affichage sur l’accueil mis à jour.");
        await fetchData();
      } catch (toggleError) {
        console.error("Erreur changement accueil album :", toggleError);
        setError("Impossible de modifier l’affichage accueil de cet album.");
      } finally {
        setActionId(null);
      }
    },
    [fetchData, showNotice],
  );

  const openDelete = useCallback(
    (id) => {
      const localItem = items.find((item) => item.id === id);

      setDeleteItem(
        localItem || {
          id,
          title: "Cet album",
        },
      );
    },
    [items],
  );

  const closeDelete = useCallback(() => {
    setDeleteItem(null);
  }, []);

  const onDelete = useCallback(async () => {
    if (!deleteItem?.id) return;

    try {
      setDeleting(true);
      setError("");

      await deleteAlbum(deleteItem.id);

      setDeleteItem(null);
      showNotice("Album supprimé.");
      await fetchData();
    } catch (deleteError) {
      console.error("Erreur suppression album :", deleteError);
      setError("Impossible de supprimer cet album.");
    } finally {
      setDeleting(false);
    }
  }, [deleteItem, fetchData, showNotice]);

  const openPhotosDrawer = useCallback(async (id) => {
    if (!id) return;

    try {
      setActionId(`photos-${id}`);
      setError("");

      const album = await getAlbumById(id);
      setPhotosAlbum(album);
    } catch (photosError) {
      console.error("Erreur ouverture photos album :", photosError);
      setError("Impossible d’ouvrir les photos de cet album.");
    } finally {
      setActionId(null);
    }
  }, []);

  const closePhotosDrawer = useCallback(() => {
    setPhotosAlbum(null);
    setMediaPickerOpen(false);
  }, []);

  const refreshPhotosAlbum = useCallback(async () => {
    if (!photosAlbum?.id) return;

    try {
      setPhotosActionId("refresh");

      const album = await getAlbumById(photosAlbum.id);
      setPhotosAlbum(album);
    } catch (refreshError) {
      console.error("Erreur rafraîchissement album :", refreshError);
      setError("Impossible d’actualiser cet album.");
    } finally {
      setPhotosActionId(null);
    }
  }, [photosAlbum]);

  const openMediaPicker = useCallback(() => {
    setMediaPickerOpen(true);
  }, []);

  const closeMediaPicker = useCallback(() => {
    setMediaPickerOpen(false);
  }, []);

  const handleAddSelectedMedias = useCallback(
    async (medias = []) => {
      if (!photosAlbum?.id) return;

      const mediaIds = Array.isArray(medias)
        ? medias
            .map((media) => Number(media?.id))
            .filter((id) => Number.isInteger(id) && id > 0)
        : [];

      if (mediaIds.length === 0) return;

      try {
        setPhotosActionId("add-medias");
        setError("");

        const album = await addItemsToAlbum(photosAlbum.id, mediaIds);

        setPhotosAlbum(album);
        setMediaPickerOpen(false);
        showNotice("Photos ajoutées à l’album.");
        await fetchData();
      } catch (addError) {
        console.error("Erreur ajout photos album :", addError);
        setError("Impossible d’ajouter ces photos à l’album.");
      } finally {
        setPhotosActionId(null);
      }
    },
    [fetchData, photosAlbum, showNotice],
  );

  const onSetCover = useCallback(
    async (mediaId) => {
      if (!photosAlbum?.id || !mediaId) return;

      try {
        setPhotosActionId(`cover-${mediaId}`);
        setError("");

        const album = await setAlbumCover(photosAlbum.id, mediaId);

        setPhotosAlbum(album);
        showNotice("Photo de couverture mise à jour.");
        await fetchData();
      } catch (coverError) {
        console.error("Erreur couverture album :", coverError);
        setError("Impossible de définir cette photo comme couverture.");
      } finally {
        setPhotosActionId(null);
      }
    },
    [fetchData, photosAlbum, showNotice],
  );

  const onToggleItemVisible = useCallback(
    async (item) => {
      if (!photosAlbum?.id || !item?.id) return;

      try {
        setPhotosActionId(`visible-${item.id}`);
        setError("");

        const album = await toggleAlbumItemVisible(photosAlbum.id, item);

        setPhotosAlbum(album);
        showNotice("Visibilité de la photo mise à jour.");
      } catch (visibleError) {
        console.error("Erreur visibilité photo :", visibleError);
        setError("Impossible de modifier la visibilité de cette photo.");
      } finally {
        setPhotosActionId(null);
      }
    },
    [photosAlbum, showNotice],
  );

  const onRemoveAlbumItem = useCallback(
    async (item) => {
      if (!photosAlbum?.id || !item?.id) return;

      try {
        setPhotosActionId(`remove-${item.id}`);
        setError("");

        const album = await removeAlbumItem(photosAlbum.id, item.id);

        setPhotosAlbum(album);
        showNotice("Photo retirée de l’album.");
        await fetchData();
      } catch (removeError) {
        console.error("Erreur retrait photo album :", removeError);
        setError("Impossible de retirer cette photo de l’album.");
      } finally {
        setPhotosActionId(null);
      }
    },
    [fetchData, photosAlbum, showNotice],
  );

  const onUpdateAlbumItem = useCallback(
    async (itemId, payload = {}) => {
      if (!photosAlbum?.id || !itemId) return;

      try {
        setPhotosActionId(`update-${itemId}`);
        setError("");

        const album = await updateAlbumItem(photosAlbum.id, itemId, payload);

        setPhotosAlbum(album);
        showNotice("Photo mise à jour.");
      } catch (updateError) {
        console.error("Erreur modification photo album :", updateError);
        setError("Impossible de modifier cette photo.");
      } finally {
        setPhotosActionId(null);
      }
    },
    [photosAlbum, showNotice],
  );

  const onReorderAlbumItems = useCallback(
    async (orderedItemIds = []) => {
      if (!photosAlbum?.id) return;

      try {
        setPhotosActionId("reorder");
        setError("");

        const album = await reorderAlbumItems(photosAlbum.id, orderedItemIds);

        setPhotosAlbum(album);
        showNotice("Ordre des photos enregistré.");
      } catch (reorderError) {
        console.error("Erreur réordonnancement album :", reorderError);
        setError("Impossible d’enregistrer le nouvel ordre.");
      } finally {
        setPhotosActionId(null);
      }
    },
    [photosAlbum, showNotice],
  );

  return {
    q,
    setQ,
    activeFilter,
    homeFilter,
    page,
    setPage,
    limit,
    items,
    total,
    totalPages,
    stats,

    loading,
    saving,
    deleting,
    actionId,
    photosActionId,
    error,
    notice,

    formOpen,
    currentId,
    form,
    fieldErrors,
    deleteItem,
    photosAlbum,
    mediaPickerOpen,

    fetchData,
    onSubmitSearch,
    changeActiveFilter,
    changeHomeFilter,
    changeLimit,
    resetFilters,

    openCreate,
    openEdit,
    closeForm,
    updateFormField,
    onSave,

    onToggleActive,
    onTogglePublic,
    onToggleHome,

    openDelete,
    closeDelete,
    onDelete,

    openPhotosDrawer,
    closePhotosDrawer,
    refreshPhotosAlbum,

    openMediaPicker,
    closeMediaPicker,
    handleAddSelectedMedias,

    onSetCover,
    onToggleItemVisible,
    onRemoveAlbumItem,
    onUpdateAlbumItem,
    onReorderAlbumItems,
  };
}